import groupBy from 'lodash/groupBy'

import type { EmployeeWithShifts, RestDay, Shift, View } from 'modules/shift_management/lib/types'
import ShiftsRow from 'modules/shift_management/store/internal/models/ShiftsRow'

export type ShiftsRowState = {
  shiftsRows: ShiftsRow[]
  paginatedShiftsRows: ShiftsRow[]
}

export function initialShiftsRowState(): ShiftsRowState {
  return { shiftsRows: [], paginatedShiftsRows: [] }
}

export type ShiftsRowsDispatch = (arg0: ShiftsRowsAction) => void

export type ShiftsRowsAction =
  | {
      type: 'BOOTSTRAP'
      shiftsRows: ShiftsRow[]
    }
  | {
      type: 'ADD_SHIFTS_ROWS'
      shiftsRows: ShiftsRow[]
    }
  | {
      type: 'REFRESH_SHIFTS_ROWS'
      shiftsRows: ShiftsRow[]
    }
  | {
      type: 'ADD_SHIFTS'
      shifts: Shift[]
      view: View
    }
  | {
      type: 'ADD_REST_DAYS'
      restDays: RestDay[]
    }
  | {
      type: 'DESTROY_SHIFTS'
      shifts: Shift[]
      view: View
    }
  | {
      type: 'DESTROY_SHIFT'
      shift: Shift
      view: View
    }
  | {
      type: 'DESTROY_REST_DAYS'
      restDays: RestDay[]
    }
  | {
      type: 'DESTROY_REST_DAY'
      restDay: RestDay
    }
  | {
      type: 'RESET_SHIFTS_ROWS'
    }
  | {
      type: 'ORDER_SHIFTS_ROWS'
      shiftsRows: ShiftsRow[]
    }
  | {
      type: 'RESET_SHIFTS'
      shifts: Shift[]
      view: View
    }

export default function reducer(state: ShiftsRowState, action: ShiftsRowsAction): ShiftsRowState {
  switch (action.type) {
    case 'BOOTSTRAP': {
      return {
        ...state,
        shiftsRows: action.shiftsRows,
        paginatedShiftsRows: action.shiftsRows,
      }
    }

    case 'ADD_SHIFTS_ROWS': {
      if (action.shiftsRows.length === 0) return state

      return {
        ...state,
        shiftsRows: [...state.shiftsRows, ...action.shiftsRows],
        paginatedShiftsRows: [...action.shiftsRows],
      }
    }

    case 'REFRESH_SHIFTS_ROWS': {
      const updatedExistingShiftsRows = state.shiftsRows.map((shiftsRow) => {
        const newShiftsRow = action.shiftsRows.find((row) => row.key === shiftsRow.key)
        const groupedEmployees =
          newShiftsRow?.employees?.reduce(
            (result: Record<number, EmployeeWithShifts>, emp: EmployeeWithShifts) => {
              result[+emp.id] = emp
              return result
            },
            {}
          ) || {}

        const newEmployees = shiftsRow.employees.map((employee: EmployeeWithShifts) => {
          return groupedEmployees[employee.id] || employee
        })

        return new ShiftsRow({ ...shiftsRow, employees: newEmployees })
      })

      const existingShiftsRowsKeys = new Set(state.shiftsRows.map((s: ShiftsRow) => s.key))
      const newShiftsRows = action.shiftsRows.filter(
        (s: ShiftsRow) => !existingShiftsRowsKeys.has(s.key)
      )

      return {
        ...state,
        shiftsRows: [...updatedExistingShiftsRows, ...newShiftsRows],
      }
    }

    case 'ADD_SHIFTS': {
      const viewShifts = action.view.isDaily
        ? action.shifts.filter((shift) => shift.isSameStartDay(action.view.firstDay))
        : action.shifts

      const groupedShifts = groupBy(viewShifts, (shift) => shift.rowKeyReference(action.view))
      const newShiftsRows = [...state.shiftsRows]

      for (const [key, shifts] of Object.entries(groupedShifts)) {
        const index = newShiftsRows.findIndex((shiftsRow) => shiftsRow.key === key)
        if (index !== -1) {
          const shiftsRow = newShiftsRows[index]
          if (shiftsRow) {
            newShiftsRows[index] = shiftsRow.addShifts(shifts)
          }
        }
      }

      return {
        ...state,
        shiftsRows: newShiftsRows,
      }
    }

    case 'ADD_REST_DAYS': {
      const groupedRestDays = groupBy(action.restDays, (restDay) => restDay.employeeId.toString())
      const newShiftsRows = [...state.shiftsRows]

      for (const [key, restDays] of Object.entries(groupedRestDays)) {
        const index = newShiftsRows.findIndex((x) => x.key === key)
        if (index !== -1) {
          const shiftsRow = newShiftsRows[index]
          if (shiftsRow) {
            newShiftsRows[index] = shiftsRow.addRestDays(restDays)
          }
        }
      }

      return {
        ...state,
        shiftsRows: newShiftsRows,
      }
    }

    case 'DESTROY_SHIFTS': {
      const groupedShifts = groupBy(action.shifts, (shift) => shift.rowKeyReference(action.view))
      const newShiftsRows = [...state.shiftsRows]

      for (const [key, shifts] of Object.entries(groupedShifts)) {
        const index = newShiftsRows.findIndex((shiftsRow) => shiftsRow.key === key)
        if (index !== -1) {
          const shiftsRow = newShiftsRows[index]
          if (shiftsRow) {
            newShiftsRows[index] = shiftsRow.destroyShifts(shifts)
          }
        }
      }

      return {
        ...state,
        shiftsRows: newShiftsRows,
      }
    }

    case 'DESTROY_SHIFT': {
      const newShiftsRows = [...state.shiftsRows]
      const index = newShiftsRows.findIndex(
        (s) => s.key === action.shift.rowKeyReference(action.view)
      )

      if (index !== -1) {
        const shiftsRow = newShiftsRows[index]
        if (shiftsRow) {
          newShiftsRows[index] = shiftsRow.destroyShift(action.shift)
        }
      }

      return {
        ...state,
        shiftsRows: newShiftsRows,
      }
    }

    case 'DESTROY_REST_DAY': {
      const newShiftsRows = [...state.shiftsRows]
      const index = newShiftsRows.findIndex((s) => s.key === action.restDay.employeeId.toString())

      if (index !== -1) {
        const shiftsRow = newShiftsRows[index]
        if (shiftsRow) {
          newShiftsRows[index] = shiftsRow.destroyRestDay(action.restDay)
        }
      }

      return {
        ...state,
        shiftsRows: newShiftsRows,
      }
    }

    case 'DESTROY_REST_DAYS': {
      const groupedRestDays = groupBy(action.restDays, (restDay) => restDay.employeeId.toString())
      const newShiftsRows = [...state.shiftsRows]

      for (const [key, restDays] of Object.entries(groupedRestDays)) {
        const index = newShiftsRows.findIndex((shiftsRow) => shiftsRow.key === key)
        if (index !== -1) {
          const shiftsRow = newShiftsRows[index]
          if (shiftsRow) {
            newShiftsRows[index] = shiftsRow.destroyRestDays(restDays)
          }
        }
      }

      return {
        ...state,
        shiftsRows: newShiftsRows,
      }
    }

    case 'RESET_SHIFTS_ROWS':
      return initialShiftsRowState()

    case 'ORDER_SHIFTS_ROWS':
      return {
        ...state,
        shiftsRows: action.shiftsRows,
      }

    case 'RESET_SHIFTS': {
      const viewShifts = action.view.isDaily
        ? action.shifts.filter((shift) => shift.isSameStartDay(action.view.firstDay))
        : action.shifts

      const groupedShifts = groupBy(viewShifts, (shift) => shift.rowKeyReference(action.view))
      const newShiftsRows = [...state.shiftsRows]

      for (const [key, shifts] of Object.entries(groupedShifts)) {
        const index = newShiftsRows.findIndex((shiftsRow) => shiftsRow.key === key)
        if (index !== -1) {
          const shiftsRow = newShiftsRows[index]
          if (shiftsRow) {
            newShiftsRows[index] = shiftsRow.resetShifts(shifts)
          }
        }
      }

      return {
        ...state,
        shiftsRows: newShiftsRows,
      }
    }

    default:
      return state
  }
}
